import type { PlatformSettings } from '@inflip/contracts/admin';
import { useCallback, useEffect, useState } from 'react';

import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { formatTime } from '../lib/time-zone';

interface PlatformSettingsState {
  session: ReturnType<typeof useAdminAuth>['session'];
  settings: PlatformSettings | null;
  setSettings: (s: PlatformSettings | null) => void;
  error: string | null;
  setError: (e: string | null) => void;
  /** API 호출 → 성공 시 최신 설정으로 교체, 실패 시 에러 배너. 성공 여부 반환 */
  run: (task: () => Promise<PlatformSettings>) => Promise<boolean>;
}

/** 결제 설정 / 홈페이지 설정 페이지가 공유하는 로드 상태 — 두 페이지 모두 같은 GET 을 읽는다 */
export function usePlatformSettings(): PlatformSettingsState {
  const { session } = useAdminAuth();
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    api
      .getPlatformSettings(session.accessToken)
      .then((s) => !cancelled && setSettings(s))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : String(err)));
    return () => {
      cancelled = true;
    };
  }, [session]);

  const run = useCallback(async (task: () => Promise<PlatformSettings>) => {
    setError(null);
    try {
      setSettings(await task());
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return false;
    }
  }, []);

  return { session, settings, setSettings, error, setError, run };
}

/** 라벨 + 입력 + 힌트 — 설정 페이지의 기본 필드 단위 */
export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-xs font-semibold text-muted-foreground">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-xs text-muted-foreground/80">{hint}</p>}
    </div>
  );
}

/** 저장 직후 잠깐 보여주는 "저장됨 HH:MM:SS" */
export function SavedAt({ at }: { at: Date | null }) {
  if (!at) return null;
  return <span className="text-xs font-semibold text-emerald-600">저장됨 {formatTime(at)}</span>;
}
